import { useCallback, useSyncExternalStore } from 'react';

const TICK_INTERVAL_MS = 60000;

const listeners = new Set<() => void>();
let intervalId: ReturnType<typeof setInterval> | null = null;
let now = Date.now();

function startInterval(): void {
    if (intervalId !== null) return;
    now = Date.now();
    intervalId = setInterval(() => {
        now = Date.now();
        listeners.forEach((listener) => listener());
    }, TICK_INTERVAL_MS);
}

function stopInterval(): void {
    if (intervalId === null) return;
    clearInterval(intervalId);
    intervalId = null;
}

function addTickListener(listener: () => void): () => void {
    listeners.add(listener);
    startInterval();
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) stopInterval();
    };
}

const noopSubscribe = (): (() => void) => () => {};

export function useRelativeTimeTick(enabled: boolean): number {
    const subscribe = useCallback(
        (listener: () => void) =>
            enabled ? addTickListener(listener) : noopSubscribe(),
        [enabled],
    );
    const getSnapshot = useCallback(() => (enabled ? now : 0), [enabled]);

    return useSyncExternalStore(subscribe, getSnapshot, () => 0);
}
